import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  DocumentData,
  QueryConstraint,
  increment,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { soundService } from './sound.service';
import {
  getActivePricingSettings,
  calculateGo7DeliveryFare,
  calculateRiderEarningsFromDeliveryFee,
  subscribePricingSettings,
} from '../utils/pricingSettings';
import {
  extractDropoffCoords,
  extractPickupCoords,
  extractLatLng,
  isValidJobCoords,
} from '../utils/jobCoordinates';

export interface RiderProfile {
  uid: string;
  name: string;
  email: string;
  phone: string;
  photoURL?: string;
  address: string;
  city: string;
  state: string;
  dateOfBirth?: string;
  nin?: string; // National ID Number
  vehicleOwnership?: 'own' | 'go7'; // Whether rider uses own or GO7-provided vehicle
  fleetType: 'rider' | 'bicycle' | 'leg-fleet';
  vehicleType: 'bike' | 'bicycle' | 'leg'; // Vehicle type for payment calculation
  rating: number;
  totalDeliveries: number;
  completedDeliveries: number; // Total completed deliveries count
  earnings: {
    total: number;
    today: number;
    week: number;
    month: number;
    byVehicleType: {
      bike: number;
      bicycle: number;
      leg: number;
    };
  };
  status: 'active' | 'inactive' | 'suspended' | 'rejected' | string;
  verificationStatus: 'pending' | 'approved' | 'rejected';
  isOnline: boolean;
  currentJobId?: string | null;
  currentOrder?: string | null;
  vehicleInfo?: {
    make: string;
    model: string;
    color: string;
    licensePlate?: string;
    driverLicense?: string;
  };
  documents?: {
    governmentId?: string;
    selfieWithId?: string;
    driverLicense?: string;
    vehicleRegistration?: string;
  };
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    accountName: string;
  };
  createdAt: Date;
  updatedAt: Date;
  approvedAt?: Date;
  approvedBy?: string;
  approved?: boolean;
  applicationStatus?: string;
  shiftStartedAt?: any;
  settings?: {
    pushNotifications?: boolean;
    soundEnabled?: boolean;
    vibrationEnabled?: boolean;
    locationAlways?: boolean;
    [key: string]: boolean | undefined;
  };
}

export interface Job {
  id: string;
  riderId?: string;
  pickupAddress: string;
  deliveryAddress: string;
  pickupLocation: { lat: number; lng: number };
  deliveryLocation: { lat: number; lng: number };
  pickupName?: string;
  pickupPhone?: string;
  customerName?: string;
  customerPhone?: string;
  pickupLandmark?: string;
  deliveryLandmark?: string;
  notes?: string;
  /** True when a customer PIN must be verified server-side before completing. */
  requiresPin?: boolean;
  isCOD?: boolean;
  codAmount?: number;
  earnings: number;
  distance: string;
  estimatedTime: string;
  packageType: string;
  /** Food/package line items for pickup checklist */
  items?: string[];
  status: 'available' | 'assigned' | 'in-progress' | 'completed' | 'cancelled';
  /** Original Firestore status — used for in-app navigation step */
  firestoreStatus?: string;
  /** Server-decided vehicle for this job (BICYCLE / EBIKE / MOTORCYCLE) */
  vehicleType?: string;
  /** Dispatch-allowed vehicle types from the order's vehicle policy */
  allowedVehicleTypes?: string[];
  /** Authoritative delivery fee stored on the order (₦) */
  deliveryFee?: number;
  createdAt: Date;
  completedAt?: Date;
}

export interface JobContacts {
  pickupName: string;
  pickupPhone: string;
  customerName: string;
  customerPhone: string;
  pickupLandmark?: string;
  deliveryLandmark?: string;
  notes?: string;
  requiresPin?: boolean;
  isCOD?: boolean;
  codAmount?: number;
}

/** Admin may set approved/status without verificationStatus — normalize for the rider app */
export function resolveRiderVerificationStatus(
  profile: Partial<RiderProfile>
): 'pending' | 'approved' | 'rejected' {
  const explicit = profile.verificationStatus;
  if (explicit === 'approved' || explicit === 'rejected') {
    return explicit;
  }
  const status = String(profile.status || '');
  if (profile.approved === true && status !== 'rejected' && status !== 'suspended') {
    return 'approved';
  }
  if (status === 'rejected' || profile.applicationStatus === 'rejected') {
    return 'rejected';
  }
  return 'pending';
}

/** How long an unassigned job stays visible on the rider dashboard */
const RIDER_POOL_VISIBILITY_MS = 60 * 60 * 1000; // 1 hour
/** After this, mark unassigned pool jobs expired in Firestore */
const RIDER_POOL_EXPIRE_MS = 60 * 60 * 1000; // 1 hour

const TERMINAL_STATUSES = new Set([
  'delivered', 'cancelled', 'completed', 'failed', 'rejected', 'rated', 'expired',
]);

const AVAILABLE_POOL_STATUSES = new Set([
  'pending', 'searching_rider', 'pending_dispatch', 'requested', 'Requested',
]);

/** Status values used in Firestore pool queries */
const POOL_QUERY_STATUSES = [
  'searching_rider',
  'pending',
  'pending_dispatch',
  'requested',
  'Requested',
] as const;

const ACTIVE_JOB_STATUSES = new Set([
  'assigned', 'accepted', 'preparing',
  'enroute_pickup', 'en_route_pickup', 'heading_to_pickup',
  'at_pickup', 'at-pickup',
  'picked_up', 'picked-up',
  'in_transit', 'in-transit', 'in_progress', 'in-progress',
  'enroute_dropoff', 'enroute', 'on_the_way', 'delivering',
  'nearby', 'at_delivery', 'at-delivery',
]);

export class FirebaseDbService {
  // Throttle warnings for missing jobs (jobId -> lastWarnedTimestamp)
  private locationUpdateWarnings = new Map<string, number>();

  private getRawStatus(data: any): string {
    return String(data?.status || data?.orderStatus || data?.dispatchStatus || '').toLowerCase();
  }

  private isTerminalStatus(status: string): boolean {
    return TERMINAL_STATUSES.has(status);
  }

  private getDocTimestampMs(value: any): number {
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  private getJobCreatedMs(data: any): number {
    return this.getDocTimestampMs(data?.createdAt || data?.timestamp);
  }

  private getJobAgeMs(data: any): number {
    const created = this.getJobCreatedMs(data);
    const ref = created || this.getDocTimestampMs(data?.updatedAt);
    return ref ? Date.now() - ref : 0;
  }

  private getJobExpiresAtMs(data: any): number {
    const explicit =
      this.getDocTimestampMs(data?.riderPoolExpiresAt) ||
      this.getDocTimestampMs(data?.expiresAt);
    if (explicit > 0) return explicit;
    const created = this.getJobCreatedMs(data);
    return created > 0 ? created + RIDER_POOL_EXPIRE_MS : 0;
  }

  private isJobExpired(data: any): boolean {
    if (this.isAcceptedOrAssignedJob(data)) return false;
    const expiresAt = this.getJobExpiresAtMs(data);
    if (expiresAt > 0) return Date.now() > expiresAt;
    const age = this.getJobAgeMs(data);
    if (age <= 0) return false;
    return age > RIDER_POOL_VISIBILITY_MS;
  }

  /** Accepted/assigned jobs stay active until admin cancels or reassigns */
  private isAcceptedOrAssignedJob(data: any): boolean {
    if (this.jobHasAssignedRider(data)) return true;
    return ACTIVE_JOB_STATUSES.has(this.getRawStatus(data));
  }

  private isFreshAvailableJob(data: any): boolean {
    const status = this.getRawStatus(data);
    if (!AVAILABLE_POOL_STATUSES.has(status)) return false;
    if (this.isTerminalStatus(status)) return false;
    if (this.isAcceptedOrAssignedJob(data)) return false;
    if (!this.jobHasValidCoordinates(data)) return false;
    return !this.isJobExpired(data);
  }

  private async enrichJobCoordinates(jobId: string, job: Job): Promise<Job> {
    const pickupOk = isValidJobCoords(job.pickupLocation);
    const dropoffOk = isValidJobCoords(job.deliveryLocation);
    if (pickupOk && dropoffOk) return job;

    const ids = [...new Set([jobId, job.id].filter(Boolean))];
    for (const id of ids) {
      try {
        const trackSnap = await getDoc(doc(db, 'deliveryTracking', id));
        if (!trackSnap.exists()) continue;
        const data = trackSnap.data() as Record<string, unknown>;
        const pickup = extractPickupCoords(data) || extractLatLng(data.pickup);
        const dropoff = extractDropoffCoords(data) || extractLatLng(data.dropoff);
        if (pickup && !pickupOk) job.pickupLocation = pickup;
        if (dropoff && !dropoffOk) job.deliveryLocation = dropoff;
        if (isValidJobCoords(job.pickupLocation) && isValidJobCoords(job.deliveryLocation)) {
          return job;
        }
      } catch (_) {}
    }
    return job;
  }

  private jobHasValidCoordinates(data: any): boolean {
    return (
      isValidJobCoords(extractPickupCoords(data)) &&
      isValidJobCoords(extractDropoffCoords(data))
    );
  }

  private async mirrorJobPatch(jobId: string, patch: Record<string, unknown>): Promise<void> {
    const withTs = { ...patch, updatedAt: patch.updatedAt || Timestamp.now() };
    for (const colName of ['deliveries', 'bookings', 'orders', 'fuelOrders'] as const) {
      try {
        const ref = doc(db, colName, jobId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          await updateDoc(ref, withTs);
        }
      } catch (_) {}
    }
    try {
      await setDoc(
        doc(db, 'deliveryTracking', jobId),
        { ...withTs, orderId: jobId },
        { merge: true }
      );
    } catch (_) {}
  }

  private buildExpiredJobPatch(now: Timestamp) {
    return {
      status: 'expired',
      orderStatus: 'expired',
      dispatchStatus: 'expired',
      expiredAt: now,
      updatedAt: now,
    };
  }

  /**
   * On login / home load: expire only unassigned pool jobs past the 1-hour window.
   * Accepted/assigned jobs are never auto-expired — admin must cancel or reassign.
   */
  async clearStaleRiderData(riderId: string): Promise<{ released: number; expired: number }> {
    let expired = 0;
    const now = Timestamp.now();

    for (const colName of ['bookings', 'orders', 'deliveries']) {
      try {
        const snap = await getDocs(
          query(
            collection(db, colName),
            where('status', 'in', [...POOL_QUERY_STATUSES]),
            limit(60)
          )
        );
        for (const docSnap of snap.docs) {
          const data = docSnap.data();
          if (this.isAcceptedOrAssignedJob(data)) continue;
          if (!this.isJobExpired(data)) continue;

          await updateDoc(doc(db, colName, docSnap.id), this.buildExpiredJobPatch(now));
          expired += 1;
        }
      } catch (e) {
        console.warn(`expire stale pool ${colName}:`, e);
      }
    }

    return { released: 0, expired };
  }

  // ========== RIDER PROFILE ==========
  
  async createRiderProfile(uid: string, data: Partial<RiderProfile>): Promise<void> {
    const riderRef = doc(db, 'riders', uid);
    
    // Remove undefined fields (Firestore doesn't support undefined)
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== undefined)
    );
    
    await setDoc(riderRef, {
      uid,
      ...cleanData,
      vehicleType: data.vehicleType || 'bicycle', // Default to bicycle if not specified
      verificationStatus: 'pending', // Requires admin approval
      status: 'inactive', // Inactive until approved
      rating: 0,
      totalDeliveries: 0,
      completedDeliveries: 0,
      earnings: { 
        total: 0, 
        today: 0, 
        week: 0, 
        month: 0,
        byVehicleType: {
          bike: 0,
          bicycle: 0,
          leg: 0
        }
      },
      isOnline: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  async getRiderProfile(uid: string): Promise<RiderProfile | null> {
    const riderRef = doc(db, 'riders', uid);
    const riderSnap = await getDoc(riderRef);
    
    if (riderSnap.exists()) {
      const data = riderSnap.data() as RiderProfile;
      return {
        ...data,
        verificationStatus: resolveRiderVerificationStatus(data),
      };
    }
    return null;
  }

  /** Live updates when admin approves — no 30s wait */
  subscribeToRiderProfile(
    uid: string,
    callback: (profile: RiderProfile | null) => void
  ): () => void {
    const riderRef = doc(db, 'riders', uid);
    return onSnapshot(
      riderRef,
      (snap) => {
        if (!snap.exists()) {
          callback(null);
          return;
        }
        const data = snap.data() as RiderProfile;
        callback({
          ...data,
          verificationStatus: resolveRiderVerificationStatus(data),
        });
      },
      (error) => {
        console.error('Rider profile listener error:', error);
      }
    );
  }

  async updateRiderProfile(uid: string, data: Partial<RiderProfile>): Promise<void> {
    const riderRef = doc(db, 'riders', uid);
    await updateDoc(riderRef, {
      ...data,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Update rider statistics after completing a delivery
   * Increments completed deliveries count and vehicle type earnings
   */
  async updateRiderStats(
    riderId: string,
    vehicleType: 'bike' | 'bicycle' | 'leg',
    earnings: number
  ): Promise<void> {
    try {
      const riderRef = doc(db, 'riders', riderId);
      
      // Increment completed deliveries and vehicle type earnings
      await updateDoc(riderRef, {
        completedDeliveries: increment(1),
        totalDeliveries: increment(1),
        [`earnings.byVehicleType.${vehicleType}`]: increment(earnings),
        updatedAt: Timestamp.now(),
      });
      
      console.log(`✅ Updated rider stats: +1 delivery, +₦${earnings} to ${vehicleType} earnings`);
    } catch (error) {
      console.error('Error updating rider stats:', error);
      throw error;
    }
  }

  // ========== JOBS ==========
  
  private getJobDistanceKm(data: any): number {
    const raw = data?.distanceKm ?? data?.distance ?? 0;
    return typeof raw === 'number' ? raw : parseFloat(String(raw)) || 0;
  }

  private jobHasAssignedRider(data: any): boolean {
    return Boolean(
      data?.riderId ||
      data?.rider?.id ||
      data?.rider?.riderId ||
      data?.assignedRider?.id
    );
  }

  private buildTrackingSeedFromJobData(
    jobId: string,
    data: any,
    riderId?: string
  ): Record<string, unknown> {
    const pickupLat =
      data?.pickup?.lat ?? data?.pickupCoords?.lat ?? data?.pickup?.coordinates?.lat ?? data?.stationLocation?.lat ?? 0;
    const pickupLng =
      data?.pickup?.lng ?? data?.pickupCoords?.lng ?? data?.pickup?.coordinates?.lng ?? data?.stationLocation?.lng ?? 0;
    const dropoffLat =
      data?.dropoff?.lat ?? data?.dropoffCoords?.lat ?? data?.dropoff?.coordinates?.lat ?? data?.deliveryLocation?.lat ?? 0;
    const dropoffLng =
      data?.dropoff?.lng ?? data?.dropoffCoords?.lng ?? data?.dropoff?.coordinates?.lng ?? data?.deliveryLocation?.lng ?? 0;

    const seed: Record<string, unknown> = { orderId: jobId };
    if (riderId) seed.riderId = riderId;
    if (Math.abs(pickupLat) > 0.01 && Math.abs(pickupLng) > 0.01) {
      const pickupAddr = this.normalizeAddress(
        data?.pickupAddress,
        data?.pickup,
        data?.stationLocation,
        data?.vendor
      );
      seed.pickup = {
        lat: pickupLat,
        lng: pickupLng,
        address: pickupAddr,
      };
      seed.pickupAddress = pickupAddr;
    }
    if (Math.abs(dropoffLat) > 0.01 && Math.abs(dropoffLng) > 0.01) {
      const dropoffAddr = this.normalizeAddress(
        data?.dropoffAddress,
        data?.dropoff,
        data?.deliveryLocation,
        data?.customer
      );
      seed.dropoff = {
        lat: dropoffLat,
        lng: dropoffLng,
        address: dropoffAddr,
      };
      seed.dropoffAddress = dropoffAddr;
    }
    return seed;
  }

  private parseJobCreatedAt(value: any): Date {
    if (!value) return new Date();
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (typeof value?.toMillis === 'function') return new Date(value.toMillis());
    return new Date(value);
  }

  private firstNonEmpty(...values: any[]): string {
    for (const value of values) {
      if (value == null || value === undefined) continue;
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number' && value !== 0) return String(value).trim();
      // Objects/arrays would otherwise become '[object Object]'; skip them
    }
    return '';
  }

  private normalizeAddress(...candidates: any[]): string {
    for (const value of candidates) {
      if (value == null) continue;
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'object' && value !== null) {
        if (typeof value.address === 'string' && value.address.trim()) return value.address.trim();
        if (typeof value.formatted_address === 'string' && value.formatted_address.trim()) return value.formatted_address.trim();
      }
    }
    return '';
  }

  private extractJobContacts(data: any): JobContacts {
    const pickupName =
      this.firstNonEmpty(
        data.pickupName,
        data.pickup?.name,
        data.pickup?.contactName,
        data.sender?.name,
        data.senderName,
        data.vendor?.name,
        data.restaurant?.name,
        data.pickupContactName,
        data.pickup?.label
      ) || 'Pickup contact';

    const pickupPhone = this.firstNonEmpty(
      data.pickupPhone,
      data.pickup?.phone,
      data.pickup?.contactPhone,
      data.sender?.phone,
      data.senderPhone,
      data.pickupContactPhone,
      data.vendor?.phone,
      data.restaurant?.phone,
      data.pickupContact,
      data.senderPhoneNumber
    );

    const customerName =
      this.firstNonEmpty(
        data.recipient?.name,
        data.recipientName,
        data.receiver?.name,
        data.dropoff?.name,
        data.dropoff?.contactName,
        data.deliveryContactName,
        data.customerName,
        data.customer?.name,
        data.userName,
        data.user?.name
      ) || 'Customer';

    const customerPhone = this.firstNonEmpty(
      data.recipient?.phone,
      data.recipientPhone,
      data.recipientNumber,
      data.receiver?.phone,
      data.dropoff?.phone,
      data.dropoff?.contactPhone,
      data.deliveryPhone,
      data.receiverPhone,
      data.customerPhone,
      data.customer?.phone,
      data.userPhone,
      data.user?.phone,
      data.phoneNumber
    );

    return {
      pickupName,
      pickupPhone,
      customerName,
      customerPhone,
      pickupLandmark: this.firstNonEmpty(
        data.pickupLandmark,
        data.pickup?.landmark,
        data.pickup?.note,
        data.pickup?.instructions
      ),
      deliveryLandmark: this.firstNonEmpty(
        data.deliveryLandmark,
        data.dropoff?.landmark,
        data.dropoff?.note,
        data.dropoff?.instructions
      ),
      notes: this.firstNonEmpty(
        data.notes,
        data.specialInstructions,
        data.instructions,
        data.deliveryNote,
        data.deliveryInstructions
      ),
      requiresPin: Boolean(
        data.requiresDeliveryPin ||
        data.deliveryPIN ||
        data.deliveryPin ||
        data.customerPIN ||
        data.pin ||
        data.dropoff?.pin ||
        data.receiverPin
      ),
      isCOD: Boolean(data.isCOD || data.cod || data.paymentMethod === 'cod'),
      codAmount: Number(data.codAmount || data.cod || 0) || undefined,
    };
  }

  // Helper: Map customer booking (simple delivery) to rider job format
  private mapBookingToJob(booking: any): Job {
    const pickupPoint = extractPickupCoords(booking);
    const dropoffPoint = extractDropoffCoords(booking);

    if (!pickupPoint || !dropoffPoint) {
      console.warn(`⚠️ Invalid coordinates for booking ${booking.id}:`, {
        pickup: booking.pickup || booking.pickupCoords,
        dropoff: booking.dropoff || booking.dropoffCoords,
      });
    }

    const pickupAddress =
      this.normalizeAddress(booking.pickupAddress, booking.pickup) ||
      this.firstNonEmpty(booking.pickup?.area) ||
      'Pickup location';
    const deliveryAddress =
      this.normalizeAddress(booking.dropoffAddress, booking.dropoff) ||
      this.firstNonEmpty(booking.dropoff?.area) ||
      'Delivery location';
    const distanceKm = this.getJobDistanceKm(booking);
    const etaMins =
      booking.etaMins ||
      booking.durationMin ||
      Math.max(10, Math.round(distanceKm * 4));
    const contacts = this.extractJobContacts(booking);

    return {
      id: booking.id || booking.bookingId,
      pickupAddress: pickupAddress || 'Pickup location',
      deliveryAddress: deliveryAddress || 'Delivery location',
      pickupLocation: pickupPoint || { lat: 0, lng: 0 },
      deliveryLocation: dropoffPoint || { lat: 0, lng: 0 },
      ...contacts,
      earnings: booking.fare || booking.finalFare || booking.total || 0,
      distance: `${distanceKm.toFixed(1)} km`,
      estimatedTime: `${etaMins} mins`,
      packageType: booking.service || booking.packageType || 'parcel',
      vehicleType: booking.vehicleType || booking.preferredVehicleType || undefined,
      allowedVehicleTypes: Array.isArray(booking.allowedVehicleTypes) && booking.allowedVehicleTypes.length
        ? booking.allowedVehicleTypes
        : Array.isArray(booking.eligibleVehicles) ? booking.eligibleVehicles : undefined,
      deliveryFee: Number(booking.deliveryFee ?? booking.feeBreakdown?.deliveryFee ?? 0) || undefined,
      status: this.mapBookingStatus(booking.status),
      createdAt: this.parseJobCreatedAt(booking.createdAt || booking.timestamp)
    };
  }

    /** Build human-readable pickup checklist from food order line items. */
  private formatOrderItemsForRider(order: any): string[] {
    const raw = Array.isArray(order?.items) ? order.items : [];
    const fromVendorOrders =
      !raw.length && order?.vendorOrders && typeof order.vendorOrders === 'object'
        ? Object.values(order.vendorOrders).flatMap((v: any) =>
            Array.isArray(v?.items) ? v.items : []
          )
        : [];
    const list = raw.length ? raw : fromVendorOrders;
    return list
      .map((item: any) => {
        if (typeof item === 'string') return item;
        const name = this.firstNonEmpty(item?.name, item?.productName, item?.title, item?.itemName) || 'Item';
        const qty = Number(item?.quantity ?? item?.qty) || 1;
        const addons = item?.addons || item?.addOns || item?.selectedAddons || [];
        const addonNames = Array.isArray(addons)
          ? addons
              .map((a: any) =>
                typeof a === 'string' ? a : a?.name || a?.title || a?.label
              )
              .filter(Boolean)
          : [];
        let line = qty > 1 ? `${qty}x ${name}` : String(name);
        if (addonNames.length) line += ` (${addonNames.join(', ')})`;
        return line;
      })
      .filter(Boolean);
  }

  // Helper: Map customer order (food/products) to rider job format
  private mapOrderToJob(order: any): Job {
    // Validate coordinates
    const pickupLat = order.pickup?.lat || order.vendor?.coordinates?.lat || order.vendorLat || 0;
    const pickupLng = order.pickup?.lng || order.vendor?.coordinates?.lng || order.vendorLng || 0;
    const dropoffLat = order.dropoff?.lat || order.customerLat || 0;
    const dropoffLng = order.dropoff?.lng || order.customerLng || 0;
    
    if (pickupLat === 0 || pickupLng === 0 || dropoffLat === 0 || dropoffLng === 0) {
      console.warn(`⚠️ Invalid coordinates for order ${order.orderId || order.id}:`, {
        pickup: { lat: pickupLat, lng: pickupLng },
        dropoff: { lat: dropoffLat, lng: dropoffLng }
      });
    }

    const contacts = this.extractJobContacts(order);
    const itemLines = this.formatOrderItemsForRider(order);
    
    return {
      id: order.id || order.orderId,
      pickupAddress: this.normalizeAddress(order.vendor, order.pickup, order.vendorAddress) || '',
      deliveryAddress: this.normalizeAddress(order.dropoff, order.customer, order.customerAddress) || '',
      pickupLocation: { 
        lat: pickupLat, 
        lng: pickupLng 
      },
      deliveryLocation: { 
        lat: dropoffLat, 
        lng: dropoffLng 
      },
      ...contacts,
      earnings: order.deliveryFee || order.total * 0.2 || 0,
      distance: `${(order.distance || 0).toFixed(1)} km`,
      estimatedTime: `${order.estimatedDeliveryTime || 30} mins`,
      packageType: itemLines.length > 0
        ? `Food Order (${itemLines.length} items)`
        : (order.orderType || order.packageType || 'Food Delivery'),
      items: itemLines,
      vehicleType: order.vehicleType || order.preferredVehicleType || undefined,
      allowedVehicleTypes: Array.isArray(order.allowedVehicleTypes) && order.allowedVehicleTypes.length
        ? order.allowedVehicleTypes
        : Array.isArray(order.eligibleVehicles) ? order.eligibleVehicles : undefined,
      deliveryFee: Number(order.deliveryFee ?? order.feeBreakdown?.deliveryFee ?? 0) || undefined,
      status: this.mapOrderStatus(order.status || order.orderStatus),
      createdAt: order.createdAt ? new Date(order.createdAt) : new Date()
    };
  }

  // Helper: Map customer booking status to rider status
  private mapBookingStatus(customerStatus: string): Job['status'] {
    const normalized = String(customerStatus || 'pending').toLowerCase().replace(/\s+/g, '_');
    const statusMap: Record<string, Job['status']> = {
      'pending': 'available',
      'searching_rider': 'available',
      'pending_dispatch': 'available',
      'requested': 'available',
      'assigned': 'assigned',
      'accepted': 'assigned',
      'enroute_pickup': 'assigned',
      'en_route_pickup': 'assigned',
      'heading_to_pickup': 'assigned',
      'at_pickup': 'assigned',
      'at-pickup': 'assigned',
      'picked_up': 'in-progress',
      'picked-up': 'in-progress',
      'in_transit': 'in-progress',
      'in-transit': 'in-progress',
      'enroute_dropoff': 'in-progress',
      'enroute': 'in-progress',
      'nearby': 'in-progress',
      'at_delivery': 'in-progress',
      'at-delivery': 'in-progress',
      'delivering': 'in-progress',
      'active': 'in-progress',
      'delivered': 'completed',
      'completed': 'completed',
      'cancelled': 'cancelled',
      'expired': 'cancelled',
    };
    return statusMap[normalized] || 'available';
  }

  // Helper: Map customer order status to rider status
  private mapOrderStatus(customerStatus: string): Job['status'] {
    const normalized = String(customerStatus || '').toLowerCase();
    const statusMap: Record<string, Job['status']> = {
      'requested': 'available',
      'pending': 'available',
      'pending_vendor': 'available',
      'searching_rider': 'available',
      'assigned': 'assigned',
      'accepted': 'assigned',
      'enroute_pickup': 'assigned',
      'en_route_pickup': 'assigned',
      'heading_to_pickup': 'assigned',
      'at_pickup': 'assigned',
      'at-pickup': 'assigned',
      'picked_up': 'in-progress',
      'picked-up': 'in-progress',
      'in_transit': 'in-progress',
      'in-transit': 'in-progress',
      'enroute_dropoff': 'in-progress',
      'enroute': 'in-progress',
      'on_the_way': 'in-progress',
      'nearby': 'in-progress',
      'at_delivery': 'in-progress',
      'at-delivery': 'in-progress',
      'delivering': 'in-progress',
      'active': 'in-progress',
      'delivered': 'completed',
      'completed': 'completed',
      'cancelled': 'cancelled',
      'expired': 'cancelled',
    };
    return statusMap[normalized] || 'available';
  }

  // Helper: Map fuel / CNG order to rider job format
  private mapFuelOrderToJob(order: any): Job {
    const pickup = extractPickupCoords(order) || { lat: 0, lng: 0 };
    const dropoff = extractDropoffCoords(order) || { lat: 0, lng: 0 };
    const fuelType = String(order.fuelType || 'fuel').toUpperCase();
    const volume = Number(order.volume) || 0;
    const unit = order.fuelType === 'cng' ? 'KG' : 'Litres';
    const amount = Number(order.amount) || 0;
    const distanceKm = this.getJobDistanceKm(order);
    const etaMins = Math.max(10, Math.round(distanceKm * 4));

    return {
      id: order.id || order.orderId,
      pickupAddress: this.normalizeAddress(order.stationAddress, order.stationLocation, order.stationName) || 'Fuel station',
      deliveryAddress: this.normalizeAddress(order.deliveryLocation, order.customer) || 'Customer location',
      pickupLocation: pickup,
      deliveryLocation: dropoff,
      customerName: this.firstNonEmpty(order.customerName, order.customer?.name, order.customer?.contactName) || 'Customer',
      customerPhone: this.firstNonEmpty(order.customerPhone, order.customer?.phone, order.customer?.contactPhone, order.phoneNumber) || '',
      requiresPin: Boolean(order.requiresDeliveryPin || order.customerPIN || order.deliveryPIN || order.deliveryPin),
      earnings: order.totalDeliveryFee || order.deliveryFee || 0,
      distance: `${distanceKm.toFixed(1)} km`,
      estimatedTime: `${etaMins} mins`,
      packageType: `⛽ ${fuelType} - ${volume} ${unit}`,
      items: amount > 0 ? [`₦${amount.toLocaleString()} ${fuelType}`] : [],
      status: this.mapOrderStatus(order.status || order.orderStatus),
      createdAt: order.createdAt ? this.parseJobCreatedAt(order.createdAt) : new Date()
    };
  }
  
  /**
   * Live sync with admin pricing (launch / standard fares on go7.ng)
   */
  initPricingSync(onChange?: () => void): () => void {
    return subscribePricingSettings(() => onChange?.());
  }

  /**
   * Rider payout from current Firestore pricing settings
   */
  calculateRiderPayout(vehicleType: 'bike' | 'bicycle' | 'leg'): number {
    const settings = getActivePricingSettings();
    return calculateRiderEarningsFromDeliveryFee(settings.minimumDeliveryFee, vehicleType, settings);
  }

  /** Rider share — stored riderPayout, else % of delivery fee from live pricing settings */
  resolveJobEarnings(data: any, vehicleType: 'bike' | 'bicycle' | 'leg'): number {
    const settings = getActivePricingSettings();
    const stored = Number(data?.riderPayout);
    if (stored > 0) return Math.round(stored);

    let deliveryFee = Number(
      data?.deliveryFee ?? data?.fare ?? data?.finalFare ?? data?.feeBreakdown?.deliveryFee ?? 0
    );
    // Parcel grandTotal includes platform fee — never use as delivery fee
    if (!deliveryFee && data?.orderType === 'parcel') {
      deliveryFee = Number(data?.fare ?? data?.finalFare ?? 0);
    }

    if (deliveryFee > 0) {
      return calculateRiderEarningsFromDeliveryFee(deliveryFee, vehicleType, settings);
    }

    const distanceKm = this.getJobDistanceKm(data);
    if (distanceKm > 0) {
      const computed = calculateGo7DeliveryFare(settings, { distanceKm });
      return calculateRiderEarningsFromDeliveryFee(computed.total, vehicleType, settings);
    }

    return this.calculateRiderPayout(vehicleType);
  }

  async getJobById(
    jobId: string,
    vehicleType: 'bike' | 'bicycle' | 'leg' = 'bicycle',
    riderId?: string
  ): Promise<Job | null> {
    if (!jobId) return null;

    const collections: Array<{ name: string; mapper: (data: any) => Job }> = [
      { name: 'bookings', mapper: (data) => this.mapBookingToJob(data) },
      { name: 'orders', mapper: (data) => this.mapOrderToJob(data) },
      { name: 'deliveries', mapper: (data) => this.mapBookingToJob(data) },
      { name: 'fuelOrders', mapper: (data) => this.mapFuelOrderToJob(data) },
    ];

    for (const { name, mapper } of collections) {
      try {
        const snap = await getDoc(doc(db, name, jobId));
        if (!snap.exists()) continue;
        const data: any = { id: snap.id, ...snap.data() };
        const job = this.buildJobFromDoc(data, mapper, vehicleType, riderId);
        if (job) return await this.enrichJobCoordinates(jobId, job);
      } catch (_) {}
    }

    for (const colName of ['bookings', 'orders', 'fuelOrders'] as const) {
      try {
        const snap = await getDocs(
          query(collection(db, colName), where('bookingId', '==', jobId), limit(1))
        );
        if (snap.empty) continue;
        const docSnap = snap.docs[0];
        const data: any = { id: docSnap.id, ...docSnap.data() };
        const mapper =
          colName === 'orders'
            ? (d: any) => this.mapOrderToJob(d)
            : colName === 'fuelOrders'
            ? (d: any) => this.mapFuelOrderToJob(d)
            : (d: any) => this.mapBookingToJob(d);
        const job = this.buildJobFromDoc(data, mapper, vehicleType, riderId);
        if (job) return await this.enrichJobCoordinates(jobId, job);
      } catch (_) {}
    }

    return null;
  }

  private buildJobFromDoc(
    data: any,
    mapper: (data: any) => Job,
    vehicleType: 'bike' | 'bicycle' | 'leg',
    riderId?: string
  ): Job | null {
    const status = this.getRawStatus(data);
    if (this.isTerminalStatus(status)) return null;

    if (this.jobHasAssignedRider(data)) {
      const assignedId = data.riderId || data.rider?.id;
      if (assignedId && riderId && assignedId !== riderId) return null;
      if (assignedId && !riderId) return null;
    }

    const job = mapper(data);
    // Always prefer Firestore document ID for writes (customer tracking listens on docId)
    if (data.id) job.id = data.id;
    job.firestoreStatus = this.getRawStatus(data);
    job.earnings = this.resolveJobEarnings(data, vehicleType);
    return job;
  }

  async markDeliveryPinVerified(jobId: string, riderId: string): Promise<void> {
    const now = Timestamp.now();
    await this.mirrorJobPatch(jobId, {
      deliveryPINVerified: true,
      deliveryPINVerifiedAt: now,
      deliveryPINVerifiedBy: riderId,
    });
  }

  async getAvailableJobs(limit_count: number = 10, vehicleType: 'bike' | 'bicycle' | 'leg' = 'bicycle'): Promise<Job[]> {
    try {
      const allJobs: Job[] = [];

      // 1. Book Delivery jobs (bookings collection)
      try {
        const bookingsRef = collection(db, 'bookings');
        const bookingsQuery = query(
          bookingsRef,
          where('status', 'in', [...POOL_QUERY_STATUSES]),
          limit(limit_count * 3)
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        bookingsSnapshot.docs.forEach((bookingDoc) => {
          const data = bookingDoc.data();
          if (!this.isFreshAvailableJob(data)) return;
          const job = this.mapBookingToJob({ id: bookingDoc.id, ...data });
          job.earnings = this.resolveJobEarnings(data, vehicleType);
          allJobs.push(job);
        });
      } catch (error) {
        console.warn('Bookings query failed (may need Firestore index):', error);
      }

      // 2. Simple delivery jobs
      try {
        const deliveriesRef = collection(db, 'deliveries');
        const deliveriesQuery = query(
          deliveriesRef,
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc'),
          limit(limit_count * 3)
        );
        const deliveriesSnapshot = await getDocs(deliveriesQuery);
        deliveriesSnapshot.docs.forEach((deliveryDoc) => {
          const data = deliveryDoc.data();
          if (!this.isFreshAvailableJob(data)) return;
          const job = this.mapBookingToJob({ id: deliveryDoc.id, ...data });
          job.earnings = this.resolveJobEarnings(data, vehicleType);
          allJobs.push(job);
        });
      } catch (error) {
        console.warn('Deliveries query failed:', error);
      }

      // 3. Food/product orders and mirrored book-delivery orders
      try {
        const ordersRef = collection(db, 'orders');
        const ordersQuery = query(
          ordersRef,
          where('status', 'in', [...POOL_QUERY_STATUSES]),
          limit(limit_count * 3)
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        ordersSnapshot.docs.forEach((orderDoc) => {
          const data = orderDoc.data();
          if (!this.isFreshAvailableJob(data)) return;
          const job = this.mapOrderToJob({ id: orderDoc.id, ...data });
          job.earnings = this.resolveJobEarnings(data, vehicleType);
          allJobs.push(job);
        });
      } catch (error) {
        console.warn('Orders query failed:', error);
      }

      // 4. Fuel / CNG orders
      try {
        const fuelRef = collection(db, 'fuelOrders');
        const fuelQuery = query(
          fuelRef,
          where('status', '==', 'pending'),
          limit(limit_count * 3)
        );
        const fuelSnapshot = await getDocs(fuelQuery);
        fuelSnapshot.docs.forEach((fuelDoc) => {
          const data = fuelDoc.data();
          if (!this.isFreshAvailableJob(data)) return;
          const job = this.mapFuelOrderToJob({ id: fuelDoc.id, ...data });
          job.earnings = this.resolveJobEarnings(data, vehicleType);
          allJobs.push(job);
        });
      } catch (error) {
        console.warn('Fuel orders query failed:', error);
      }

      // Deduplicate by id (bookings + orders share the same Go7- code)
      const uniqueJobs = new Map<string, Job>();
      allJobs.forEach((job) => {
        if (!uniqueJobs.has(job.id)) uniqueJobs.set(job.id, job);
      });

      const suitableJobs = [...uniqueJobs.values()].filter(job => {
        if (job.status !== 'available') return false;
        const distanceKm = parseFloat(job.distance.replace(/[^\d.]/g, ''));

        // Server-side vehicle policy (when present) is authoritative:
        // bike→MOTORCYCLE, bicycle→BICYCLE, leg-fleet→BICYCLE (≤4 km walking cap).
        const allowed = job.allowedVehicleTypes;
        if (Array.isArray(allowed) && allowed.length) {
          const riderVehicle = vehicleType === 'bike' ? 'MOTORCYCLE' : 'BICYCLE';
          if (!allowed.includes(riderVehicle)) return false;
          if (vehicleType === 'leg' && distanceKm > 4) return false;
          return true;
        }
        if (job.vehicleType && job.vehicleType !== 'ANY') {
          const riderVehicle = vehicleType === 'bike' ? 'MOTORCYCLE' : 'BICYCLE';
          if (job.vehicleType !== riderVehicle) return false;
          if (vehicleType === 'leg' && distanceKm > 4) return false;
          return true;
        }

        if (distanceKm <= 1.5) return true;
        if (distanceKm <= 4) return vehicleType === 'bicycle' || vehicleType === 'bike';
        return vehicleType === 'bike';
      });

      return suitableJobs
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, limit_count);
    } catch (error) {
      console.error('Error fetching available jobs:', error);
      return [];
    }
  }

  /**
   * Real-time refresh when pool jobs change (bookings/orders).
   * Returns unsubscribe — call on HomeScreen unmount or when going offline.
   */
  subscribeToAvailableJobs(
    limit_count: number,
    vehicleType: 'bike' | 'bicycle' | 'leg',
    onJobs: (jobs: Job[]) => void
  ): () => void {
    let cancelled = false;
    const refresh = async () => {
      if (cancelled) return;
      const jobs = await this.getAvailableJobs(limit_count, vehicleType);
      if (!cancelled) onJobs(jobs);
    };

    const poolQuery = (col: string) =>
      query(collection(db, col), where('status', 'in', [...POOL_QUERY_STATUSES]), limit(100));

    const unsubs = [
      onSnapshot(poolQuery('bookings'), () => { void refresh(); }, () => { void refresh(); }),
      onSnapshot(poolQuery('orders'), () => { void refresh(); }, () => { void refresh(); }),
      onSnapshot(poolQuery('deliveries'), () => { void refresh(); }, () => { void refresh(); }),
      onSnapshot(query(collection(db, 'fuelOrders'), where('status', '==', 'pending'), limit(100)), () => { void refresh(); }, () => { void refresh(); }),
    ];

    void refresh();
    return () => {
      cancelled = true;
      unsubs.forEach((u) => u());
    };
  }

  private async markRiderOnJob(riderId: string, jobId: string): Promise<void> {
    await updateDoc(doc(db, 'riders', riderId), {
      status: 'delivering',
      isAvailable: false,
      currentJobId: jobId,
      currentOrder: jobId,
      updatedAt: Timestamp.now(),
    });
  }

  private buildRiderPayload(riderId: string, riderProfile: RiderProfile | null) {
    return {
      id: riderId,
      name: riderProfile?.name || 'Rider',
      phone: riderProfile?.phone || '',
      code: `R-${riderId.substring(0, 6).toUpperCase()}`,
    };
  }

  /**
   * Public entry point. Silences the repeating new-job alert the moment the
   * rider engages with a job, and restarts it if the accept fails so an
   * available job is never silently lost.
   */
  async acceptJob(jobId: string, riderId: string): Promise<void> {
    soundService.stopAlert();
    try {
      await this.acceptJobInner(jobId, riderId);
    } catch (error) {
      soundService.startAlert('new-job');
      throw error;
    }
  }

  private async acceptJobInner(jobId: string, riderId: string): Promise<void> {
    const riderProfile = await this.getRiderProfile(riderId);
    const riderPayload = this.buildRiderPayload(riderId, riderProfile);
    const now = Timestamp.now();
    const assignmentPayload = {
      status: 'assigned',
      dispatchStatus: 'assigned',
      orderStatus: 'assigned',
      riderId,
      riderName: riderProfile?.name || 'Rider',
      riderPhone: riderProfile?.phone || '',
      rider: riderPayload,
      acceptedAt: now,
      acceptedTime: now,
      updatedAt: now,
    };

    const assertCanAccept = (data: any) => {
      const status = this.getRawStatus(data);
      if (['delivered', 'cancelled', 'completed', 'expired'].includes(status)) {
        throw new Error('This delivery is no longer available');
      }
      const assignedId = data?.riderId || data?.rider?.id;
      if (assignedId && assignedId !== riderId) {
        throw new Error('Already accepted by another rider');
      }
    };

    const rethrowAcceptBlock = (error: unknown) => {
      if (
        error instanceof Error &&
        (error.message === 'Distance exceeds walking limit' ||
          error.message === 'Already accepted by another rider' ||
          error.message === 'This delivery is no longer available')
      ) {
        throw error;
      }
    };

    // Try deliveries collection first (simple deliveries)
    try {
      const deliveryRef = doc(db, 'deliveries', jobId);
      const deliverySnap = await getDoc(deliveryRef);

      if (deliverySnap.exists()) {
        const deliveryData = deliverySnap.data();
        assertCanAccept(deliveryData);
        const distanceKm = this.getJobDistanceKm(deliveryData);

        if (riderProfile?.vehicleType === 'leg' && distanceKm > 1.5) {
          throw new Error('Distance exceeds walking limit');
        }

        await updateDoc(deliveryRef, assignmentPayload);
        await setDoc(doc(db, 'orders', jobId), assignmentPayload, { merge: true });
        await setDoc(doc(db, 'bookings', jobId), assignmentPayload, { merge: true });
        await setDoc(
          doc(db, 'deliveryTracking', jobId),
          {
            ...assignmentPayload,
            ...this.buildTrackingSeedFromJobData(jobId, deliveryData, riderId),
          },
          { merge: true }
        );
        await this.markRiderOnJob(riderId, jobId);
        return;
      }
    } catch (error) {
      rethrowAcceptBlock(error);
      console.log('Not a delivery, trying bookings collection...');
    }

    // Book Delivery jobs live in bookings (and are mirrored to orders)
    try {
      const bookingRef = doc(db, 'bookings', jobId);
      const bookingSnap = await getDoc(bookingRef);

      if (bookingSnap.exists()) {
        const bookingData = bookingSnap.data();
        assertCanAccept(bookingData);
        const distanceKm = this.getJobDistanceKm(bookingData);

        if (riderProfile?.vehicleType === 'leg' && distanceKm > 1.5) {
          throw new Error('Distance exceeds walking limit');
        }

        await updateDoc(bookingRef, assignmentPayload);
        await setDoc(doc(db, 'orders', jobId), assignmentPayload, { merge: true });
        await setDoc(
          doc(db, 'deliveryTracking', jobId),
          {
            ...assignmentPayload,
            ...this.buildTrackingSeedFromJobData(jobId, bookingData, riderId),
          },
          { merge: true }
        );
        await this.markRiderOnJob(riderId, jobId);
        return;
      }
    } catch (error) {
      rethrowAcceptBlock(error);
      console.log('Not a booking, trying orders collection...');
    }

    // Food/product orders
    try {
      const orderRef = doc(db, 'orders', jobId);
      const orderSnap = await getDoc(orderRef);

      if (orderSnap.exists()) {
        const orderData = orderSnap.data();
        assertCanAccept(orderData);
        const distanceKm = this.getJobDistanceKm(orderData);

        if (riderProfile?.vehicleType === 'leg' && distanceKm > 1.5) {
          throw new Error('Distance exceeds walking limit');
        }

        await updateDoc(orderRef, assignmentPayload);
        await setDoc(doc(db, 'bookings', jobId), assignmentPayload, { merge: true });
        await setDoc(
          doc(db, 'deliveryTracking', jobId),
          {
            ...assignmentPayload,
            ...this.buildTrackingSeedFromJobData(jobId, orderData, riderId),
          },
          { merge: true }
        );
        await this.markRiderOnJob(riderId, jobId);
        return;
      }
    } catch (error) {
      rethrowAcceptBlock(error);
      console.error('Error accepting order:', error);
      throw error;
    }

    // Fuel / CNG orders
    try {
      const fuelRef = doc(db, 'fuelOrders', jobId);
      const fuelSnap = await getDoc(fuelRef);

      if (fuelSnap.exists()) {
        const fuelData = fuelSnap.data();
        assertCanAccept(fuelData);
        const distanceKm = this.getJobDistanceKm(fuelData);

        if (riderProfile?.vehicleType === 'leg' && distanceKm > 1.5) {
          throw new Error('Distance exceeds walking limit');
        }

        await updateDoc(fuelRef, assignmentPayload);
        await setDoc(
          doc(db, 'deliveryTracking', jobId),
          {
            ...assignmentPayload,
            ...this.buildTrackingSeedFromJobData(jobId, fuelData, riderId),
          },
          { merge: true }
        );
        await this.markRiderOnJob(riderId, jobId);
        return;
      }
    } catch (error) {
      rethrowAcceptBlock(error);
      console.error('Error accepting fuel order:', error);
      throw error;
    }

    throw new Error(`Job ${jobId} not found in deliveries, bookings, orders, or fuel orders`);
  }

  async updateJobStatus(jobId: string, status: Job['status']): Promise<void> {
    const bookingStatus =
      status === 'in-progress' ? 'in_progress' :
      status === 'completed' ? 'delivered' :
      status === 'assigned' ? 'assigned' :
      status;

    await this.mirrorJobPatch(jobId, {
      status: bookingStatus,
      orderStatus: bookingStatus,
      dispatchStatus: bookingStatus,
    });
  }

  /** Rider accepted and is heading to pickup — customer track page */
  async markEnRouteToPickup(jobId: string, riderId?: string): Promise<void> {
    const patch: Record<string, unknown> = {
      status: 'enroute_pickup',
      orderStatus: 'enroute_pickup',
      dispatchStatus: 'enroute_pickup',
    };
    if (riderId) patch.riderId = riderId;
    await this.mirrorJobPatch(jobId, patch);

    try {
      const docId = await this.resolveJobDocId(jobId);
      const job = await this.getJobById(docId, 'bike', riderId);
      if (job?.pickupLocation?.lat && job?.deliveryLocation?.lat) {
        await setDoc(
          doc(db, 'deliveryTracking', docId),
          {
            orderId: docId,
            riderId: riderId || null,
            status: 'enroute_pickup',
            pickup: {
              lat: job.pickupLocation.lat,
              lng: job.pickupLocation.lng,
              address: job.pickupAddress,
            },
            dropoff: {
              lat: job.deliveryLocation.lat,
              lng: job.deliveryLocation.lng,
              address: job.deliveryAddress,
            },
            updatedAt: Timestamp.now(),
          },
          { merge: true }
        );
      }
    } catch (e) {
      console.warn('[tracking] Failed to seed deliveryTracking coords:', e);
    }
  }

  /** Rider tapped "Arrived at Pickup" */
  async markArrivedAtPickup(jobId: string): Promise<void> {
    await this.mirrorJobPatch(jobId, {
      status: 'at_pickup',
      orderStatus: 'at_pickup',
      dispatchStatus: 'at_pickup',
      arrivedAtPickupAt: Timestamp.now(),
    });
  }

  /** Rider tapped "Arrived at Delivery" */
  async markArrivedAtDelivery(jobId: string): Promise<void> {
    await this.mirrorJobPatch(jobId, {
      status: 'at_delivery',
      orderStatus: 'at_delivery',
      dispatchStatus: 'at_delivery',
      arrivedAtDeliveryAt: Timestamp.now(),
    });
  }

  /** Customer track page: rider confirmed pickup */
  async markJobPickedUp(jobId: string): Promise<void> {
    const now = Timestamp.now();
    const patch = {
      status: 'picked_up',
      orderStatus: 'picked_up',
      dispatchStatus: 'picked_up',
      pickedUpAt: now,
      updatedAt: now,
    };
    await this.mirrorJobPatch(jobId, patch);
  }

  /** Rider left pickup — heading to dropoff */
  async markEnRouteToDelivery(jobId: string): Promise<void> {
    await this.mirrorJobPatch(jobId, {
      status: 'enroute_dropoff',
      orderStatus: 'enroute_dropoff',
      dispatchStatus: 'enroute_dropoff',
      enrouteToDeliveryAt: Timestamp.now(),
    });
  }

  async savePickupProof(
    jobId: string,
    data: { photoUrls: string[]; notes?: string }
  ): Promise<void> {
    const patch: Record<string, unknown> = {
      pickupPhotos: data.photoUrls,
      updatedAt: Timestamp.now(),
    };
    if (data.photoUrls[0]) patch.pickupPhotoURL = data.photoUrls[0];
    if (data.notes) patch.pickupNotes = data.notes;
    await this.mirrorJobPatch(jobId, patch);
  }

  async saveDeliveryProof(
    jobId: string,
    data: {
      photoUrls: string[];
      signatureURL?: string;
      cashCollected?: number;
      tip?: number;
    }
  ): Promise<void> {
    const patch: Record<string, unknown> = {
      deliveryPhotos: data.photoUrls,
      updatedAt: Timestamp.now(),
    };
    if (data.photoUrls[0]) {
      patch.deliveryPhotoURL = data.photoUrls[0];
      patch.proofPhoto = data.photoUrls[0];
    }
    if (data.signatureURL) patch.signatureURL = data.signatureURL;
    if (data.cashCollected != null) {
      patch.cashCollected = data.cashCollected;
      patch.codCollected = data.cashCollected;
    }
    if (data.tip != null) patch.tip = data.tip;
    await this.mirrorJobPatch(jobId, patch);
  }

  async clearRiderActiveJob(riderId: string): Promise<void> {
    await updateDoc(doc(db, 'riders', riderId), {
      currentJobId: null,
      currentOrder: null,
      status: 'active',
      isAvailable: true,
      updatedAt: Timestamp.now(),
    });
  }

  async completeJob(jobId: string, data: any): Promise<void> {
    const now = Timestamp.now();

    // Economics: pickup→dropoff duration for post-launch analysis. Reuse the
    // pickedUpAt already written by markJobPickedUp rather than a second event.
    let pickupToDropoffDurationMinutes: number | null = null;
    try {
      const docId = await this.resolveJobDocId(jobId);
      for (const colName of ['bookings', 'orders', 'deliveries'] as const) {
        const snap = await getDoc(doc(db, colName, docId));
        if (!snap.exists()) continue;
        const raw = snap.data()?.pickedUpAt;
        const ms =
          raw && typeof raw.toMillis === 'function' ? raw.toMillis()
          : raw && typeof raw.seconds === 'number' ? raw.seconds * 1000
          : raw ? Date.parse(raw) : NaN;
        if (Number.isFinite(ms)) {
          pickupToDropoffDurationMinutes = Math.max(0, Math.round((now.toMillis() - ms) / 60000));
          break;
        }
      }
    } catch (_) {}

    const completionPayload = {
      status: 'delivered',
      orderStatus: 'delivered',
      dispatchStatus: 'delivered',
      deliveredAt: now,
      completedTime: now,
      actualDeliveryTime: now,
      ...(pickupToDropoffDurationMinutes != null
        ? { pickupToDropoffDurationMinutes }
        : {}),
      updatedAt: now,
      ...data,
    };
    await this.mirrorJobPatch(jobId, completionPayload);
  }

  /** Resolve Firestore document ID when jobId may be a bookingId field value */
  private async resolveJobDocId(jobId: string): Promise<string> {
    if (!jobId) return jobId;
    for (const colName of ['bookings', 'orders', 'deliveries'] as const) {
      try {
        const direct = await getDoc(doc(db, colName, jobId));
        if (direct.exists()) return direct.id;
      } catch (_) {}
      try {
        const snap = await getDocs(
          query(collection(db, colName), where('bookingId', '==', jobId), limit(1))
        );
        if (!snap.empty) return snap.docs[0].id;
      } catch (_) {}
    }
    return jobId;
  }

  // Update rider location for customer tracking (works for both deliveries and orders)
  private jobDocExists = new Map<string, boolean>();

  async updateRiderLocation(jobId: string, location: { lat: number; lng: number }, riderId?: string): Promise<void> {
    const lat = Number(location.lat);
    const lng = Number(location.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const now = Timestamp.now();
    const ts = Date.now();
    const riderLocation = {
      lat,
      lng,
      latitude: lat,
      longitude: lng,
      timestamp: ts,
      updatedAt: ts,
    };

    const docId = await this.resolveJobDocId(jobId);
    const trackingIds = [...new Set([jobId, docId].filter(Boolean))];

    const trackingPatch: Record<string, unknown> = {
      riderLocation,
      currentLocation: riderLocation,
      updatedAt: now,
      lastUpdated: ts,
    };
    if (riderId) trackingPatch.riderId = riderId;

    const orderPatch: Record<string, unknown> = {
      riderLocation,
      locationUpdatedAt: now,
      updatedAt: now,
    };
    if (riderId) orderPatch.riderId = riderId;

    const errors: string[] = [];

    for (const id of trackingIds) {
      try {
        await setDoc(
          doc(db, 'deliveryTracking', id),
          { ...trackingPatch, orderId: id },
          { merge: true }
        );
      } catch (e: any) {
        errors.push(`deliveryTracking/${id}: ${e?.message || e}`);
      }

      try {
        await setDoc(
          doc(db, 'rider_locations', id),
          { ...riderLocation, riderId: riderId || null, orderId: id, timestamp: ts },
          { merge: true }
        );
      } catch (e: any) {
        errors.push(`rider_locations/${id}: ${e?.message || e}`);
      }

      for (const colName of ['bookings', 'orders', 'deliveries'] as const) {
        try {
          const ref = doc(db, colName, id);
          const cacheKey = `${colName}/${id}`;
          let exists = this.jobDocExists.get(cacheKey);
          if (exists === undefined) {
            exists = (await getDoc(ref)).exists();
            this.jobDocExists.set(cacheKey, exists);
          }
          if (exists) {
            await setDoc(ref, orderPatch, { merge: true });
          }
        } catch (e: any) {
          errors.push(`${colName}/${id}: ${e?.message || e}`);
        }
      }
    }

    if (riderId) {
      try {
        await setDoc(
          doc(db, 'riders', riderId),
          {
            currentLocation: { latitude: lat, longitude: lng },
            lat,
            lng,
            lastUpdated: now,
            lastActive: now,
            currentOrderId: docId || jobId,
          },
          { merge: true }
        );
      } catch (e: any) {
        errors.push(`riders/${riderId}: ${e?.message || e}`);
      }

      try {
        await setDoc(
          doc(db, 'riderLocations', riderId),
          {
            riderId,
            orderId: docId || jobId,
            location: riderLocation,
            ...riderLocation,
            updatedAt: now,
          },
          { merge: true }
        );
      } catch (e: any) {
        errors.push(`riderLocations/${riderId}: ${e?.message || e}`);
      }
    }

    if (errors.length) {
      console.warn('[GPS] Some location writes failed:', errors.join('; '));
    }
  }

  // ========== EARNINGS ==========
  
  async addEarning(riderId: string, amount: number, jobId: string): Promise<void> {
    const earningRef = doc(collection(db, 'earnings'));
    await setDoc(earningRef, {
      riderId,
      amount,
      jobId,
      createdAt: Timestamp.now(),
    });

    // Update rider's total earnings
    const riderRef = doc(db, 'riders', riderId);
    const riderSnap = await getDoc(riderRef);
    
    if (riderSnap.exists()) {
      const rider = riderSnap.data();
      await updateDoc(riderRef, {
        'earnings.total': (rider.earnings?.total || 0) + amount,
      });
    }
  }

  async getRiderEarnings(riderId: string): Promise<any[]> {
    const earningsRef = collection(db, 'earnings');
    const q = query(earningsRef, where('riderId', '==', riderId));
    const querySnapshot = await getDocs(q);
    const rows = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return rows.sort((a: any, b: any) => {
      const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
  }

  // ========== DELIVERY HISTORY ==========
  
  async getRiderJobHistory(riderId: string, limit_count: number = 50): Promise<Job[]> {
    const results: Job[] = [];
    const seen = new Set<string>();

    const pushIfHistory = (id: string, data: any, mapper?: (d: any) => Job) => {
      if (seen.has(id)) return;
      const status = this.getRawStatus(data);
      // Include every terminal status (delivered, completed, cancelled, failed,
      // rejected, expired, rated, …) so completed jobs are never dropped from history.
      if (!TERMINAL_STATUSES.has(status)) return;
      seen.add(id);
      results.push(mapper ? mapper({ id, ...data }) : ({ id, ...data } as Job));
    };

    // Every collection a rider job can live in — including fuel/CNG orders, which
    // were previously missing from history (the reported mismatch).
    const historyCollections: Array<{ name: string; mapper: (d: any) => Job }> = [
      { name: 'bookings', mapper: (d) => this.mapBookingToJob(d) },
      { name: 'deliveries', mapper: (d) => this.mapBookingToJob(d) },
      { name: 'orders', mapper: (d) => this.mapOrderToJob(d) },
      { name: 'fuelOrders', mapper: (d) => this.mapFuelOrderToJob(d) },
    ];

    for (const { name, mapper } of historyCollections) {
      // Primary: newest-first riderId query (composite index added in firestore.indexes.json).
      try {
        const snap = await getDocs(
          query(
            collection(db, name),
            where('riderId', '==', riderId),
            orderBy('createdAt', 'desc'),
            limit(limit_count * 3)
          )
        );
        snap.docs.forEach((d) => pushIfHistory(d.id, d.data(), mapper));
      } catch (err) {
        // Composite index not deployed yet — fall back to an unsorted fetch so
        // history still works while the requested index builds.
        try {
          const snapFallback = await getDocs(
            query(collection(db, name), where('riderId', '==', riderId), limit(limit_count * 3))
          );
          snapFallback.docs.forEach((d) => pushIfHistory(d.id, d.data(), mapper));
        } catch (_) {}
      }

      // Safety net: some jobs record the rider under `rider.id` instead of
      // `riderId`. Capture those too (deduped by doc id) so nothing is missed.
      try {
        const snapSub = await getDocs(
          query(collection(db, name), where('rider.id', '==', riderId), limit(limit_count * 3))
        );
        snapSub.docs.forEach((d) => pushIfHistory(d.id, d.data(), mapper));
      } catch (_) {}
    }

    return results
      .sort((a: any, b: any) => {
        const aTime = this.getDocTimestampMs(a.deliveredAt || a.completedAt || a.createdAt);
        const bTime = this.getDocTimestampMs(b.deliveredAt || b.completedAt || b.createdAt);
        return bTime - aTime;
      })
      .slice(0, limit_count);
  }

  // ========== CUSTOMER INFO ==========

  async getJobContacts(jobId: string): Promise<JobContacts | null> {
    const collections = ['deliveries', 'orders', 'bookings'] as const;

    for (const collectionName of collections) {
      try {
        const snap = await getDoc(doc(db, collectionName, jobId));
        if (!snap.exists()) continue;
        return this.extractJobContacts({ id: snap.id, ...snap.data() });
      } catch (_) {}
    }

    return null;
  }

  async getJobCustomerInfo(jobId: string): Promise<{ name: string; phone: string } | null> {
    const contacts = await this.getJobContacts(jobId);
    if (!contacts) return null;
    return {
      name: contacts.customerName,
      phone: contacts.customerPhone,
    };
  }

  // ========== NOTIFICATIONS ==========
  
  async addNotification(riderId: string, notification: any): Promise<void> {
    const notifRef = doc(collection(db, 'notifications'));
    await setDoc(notifRef, {
      riderId,
      ...notification,
      read: false,
      createdAt: Timestamp.now(),
    });
  }

  async getRiderNotifications(riderId: string): Promise<any[]> {
    const notifsRef = collection(db, 'notifications');
    const q = query(notifsRef, where('riderId', '==', riderId));
    const querySnapshot = await getDocs(q);
    const rows = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    const sorted = rows.sort((a: any, b: any) => {
      const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
    return sorted.slice(0, 50);
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    const notifRef = doc(db, 'notifications', notificationId);
    await updateDoc(notifRef, {
      read: true,
    });
  }

  // ========== PERFORMANCE & RATINGS ==========
  
  async updateRiderRating(riderId: string, newRating: number): Promise<void> {
    const riderRef = doc(db, 'riders', riderId);
    const riderSnap = await getDoc(riderRef);
    
    if (riderSnap.exists()) {
      const rider = riderSnap.data();
      const totalDeliveries = rider.totalDeliveries || 0;
      const currentRating = rider.rating || 0;
      
      // Calculate new average rating
      const newAverageRating = 
        ((currentRating * totalDeliveries) + newRating) / (totalDeliveries + 1);
      
      await updateDoc(riderRef, {
        rating: newAverageRating,
        totalDeliveries: totalDeliveries + 1,
      });
    }
  }

  // ========== SHIFT MANAGEMENT ==========
  
  async startShift(riderId: string): Promise<void> {
    const riderRef = doc(db, 'riders', riderId);
    const now = Timestamp.now();

    // Always persist online status first — riders must stay online even if shift log fails
    await updateDoc(riderRef, {
      isOnline: true,
      shiftStartedAt: now,
      updatedAt: now,
    });

    try {
      const shiftRef = doc(collection(db, 'shifts'));
      await setDoc(shiftRef, {
        riderId,
        startTime: now,
        status: 'active',
      });
      await updateDoc(riderRef, { activeShiftId: shiftRef.id });
    } catch (error) {
      console.warn('Shift record create failed (rider is still online):', error);
    }
  }

  async endShift(riderId: string, shiftId: string): Promise<void> {
    const shiftRef = doc(db, 'shifts', shiftId);
    await updateDoc(shiftRef, {
      endTime: Timestamp.now(),
      status: 'completed',
    });

    // Update rider online status
    const riderRef = doc(db, 'riders', riderId);
    await updateDoc(riderRef, {
      isOnline: false,
    });
  }

  /**
   * Update order with payment breakdown after delivery completion
   * @param orderId - Order ID
   * @param paymentBreakdown - Payment breakdown details
   */
  async updateOrderPaymentBreakdown(orderId: string, paymentBreakdown: any): Promise<void> {
    const patch = { paymentBreakdown, updatedAt: Timestamp.now() };
    try {
      let wrote = false;
      for (const colName of ['deliveries', 'orders', 'bookings'] as const) {
        try {
          const ref = doc(db, colName, orderId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            await updateDoc(ref, patch);
            wrote = true;
          }
        } catch (_) {}
      }
      if (!wrote) {
        console.warn(`Job ${orderId} not found in deliveries, orders, or bookings — payment breakdown not persisted`);
      }
    } catch (error) {
      console.error('Failed to update payment breakdown:', error);
      throw error;
    }
  }

  async goOffline(riderId: string): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(db, 'riders', riderId), {
      isOnline: false,
      activeShiftId: null,
      shiftStartedAt: null,
      updatedAt: now,
    });
    try {
      const shiftsSnap = await getDocs(
        query(
          collection(db, 'shifts'),
          where('riderId', '==', riderId),
          where('status', '==', 'active'),
          limit(3)
        )
      );
      for (const shiftDoc of shiftsSnap.docs) {
        await updateDoc(shiftDoc.ref, { endTime: now, status: 'completed' });
      }
    } catch (error) {
      console.warn('Could not close active shift:', error);
    }
  }
}

export const dbService = new FirebaseDbService();
